import React, { useState } from "react";
import axios from "axios";
import { Lightbulb, X, Check, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "feature", label: "New Feature" },
  { id: "bug", label: "Bug" },
  { id: "balance", label: "Game Balance" },
  { id: "ui", label: "UI / UX" },
  { id: "general", label: "Other" },
];

/**
 * Floating "How can we be better?" button — fixed bottom-left of every
 * authenticated screen. Click opens a modal with a category selector and
 * a free-text area (max 1,000 chars). Submission goes to /api/feedback.
 *
 * Mounted by AppLayout so it shows on Hub, Garage, Lobbies, Race, Bosses,
 * Rankings, etc — anywhere the player spends time.
 */
export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("feature");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const close = () => {
    setOpen(false);
    // Slight delay so the closing animation doesn't show stale state.
    setTimeout(() => {
      setText("");
      setCategory("feature");
      setSubmitted(false);
    }, 200);
  };

  const submit = async () => {
    if (text.trim().length < 4) {
      toast.error("Add a few more words so we can act on it.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/feedback`,
        { text: text.trim(), category },
        { withCredentials: true },
      );
      setSubmitted(true);
      toast.success("Thanks — we read every one.");
      // Auto-close after 1.6s so the player gets visual confirmation.
      setTimeout(close, 1600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        aria-label="How can we be better?"
        className="fixed bottom-6 left-4 lg:left-6 z-[60] group flex items-center gap-2 pl-3 pr-4 py-3 bg-[#FF3B30] text-white border-2 border-black hover:bg-[#FF6B5C] anim-pulse-red shadow-[0_8px_24px_rgba(255,59,48,0.45)] transition-all hover:-translate-y-0.5"
      >
        <Lightbulb className="w-5 h-5" strokeWidth={2.5} />
        <span className="font-heading text-xs uppercase tracking-[0.18em] hidden sm:inline">
          How can we be better?
        </span>
        <span className="font-heading text-xs uppercase tracking-[0.18em] sm:hidden">
          Feedback
        </span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-8"
          role="dialog"
          aria-modal="true"
          data-testid="feedback-modal"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
          <div className="relative max-w-lg w-full surface-glass border-2 border-[#FF3B30]">
            <div className="flex items-start justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#FF3B30]/15 border border-[#FF3B30] text-[#FF3B30]">
                  <Lightbulb className="w-5 h-5" />
                </div>
                <div>
                  <div className="label-kicker">Tell the devs</div>
                  <div className="font-heading text-xl uppercase">How can we be better?</div>
                </div>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                className="text-white/40 hover:text-white"
                data-testid="feedback-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {submitted ? (
              <div
                className="p-10 text-center"
                data-testid="feedback-success"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#34C759]/15 border-2 border-[#34C759] text-[#34C759] mb-4">
                  <Check className="w-7 h-7" strokeWidth={3} />
                </div>
                <div className="font-heading text-2xl mb-2">Thanks, racer.</div>
                <p className="text-sm text-white/60">
                  We read every tip. If it makes the game tighter, you might just see it ship.
                </p>
              </div>
            ) : (
              <div className="p-5">
                <div className="mb-4">
                  <div className="label-kicker mb-2">Category</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setCategory(c.id)}
                        data-testid={`feedback-category-${c.id}`}
                        className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                          category === c.id
                            ? "border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]"
                            : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="label-kicker">Your tip</span>
                    <span className="font-mono text-[10px] text-white/40">{text.length}/1000</span>
                  </div>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, 1000))}
                    placeholder="What's clunky? What's missing? What would make Nitro Auto Heist way more fun?"
                    rows={6}
                    data-testid="feedback-textarea"
                    className="w-full bg-black/40 border border-white/10 px-3 py-3 text-sm text-white placeholder-white/30 focus:border-[#FFCC00] outline-none resize-y leading-relaxed"
                  />
                </div>

                <div className="mt-2 text-[10px] text-white/40 leading-relaxed">
                  We read every submission. We may follow up via your account email if we have questions.
                </div>

                <button
                  onClick={submit}
                  disabled={submitting || text.trim().length < 4}
                  data-testid="feedback-submit"
                  className={`mt-5 w-full flex items-center justify-center gap-2 py-3 font-heading text-sm uppercase tracking-[0.25em] transition-all ${
                    submitting || text.trim().length < 4
                      ? "bg-white/5 text-white/30 cursor-not-allowed"
                      : "bg-[#FFCC00] text-black hover:bg-[#FFD633]"
                  }`}
                >
                  <Send className="w-4 h-4" />
                  {submitting ? "Sending…" : "Send tip"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
